/**
 * Shared utilities for org-aware field provider processing.
 * Used by both the useOrgAwareProviders hook and IssueFieldFilterQueryParser.
 */

const FIELD_PREFIX = 'field.'

/**
 * Extract org names from raw query string using regex patterns.
 * Supports both org: and repo: filters, extracting org from repo format.
 *
 * @param rawQuery - The raw search query string
 * @returns Array of org names found in the query
 *
 * @example
 * extractOrgsFromRawQuery('org:github repo:microsoft/repo')
 * // returns ['github', 'microsoft']
 */
export function extractOrgsFromRawQuery(rawQuery: string): string[] {
  const orgs = new Set<string>()

  // Match org patterns like: org:github, org:"github-enterprise"
  const orgMatches = rawQuery.match(/org:"?([a-zA-Z0-9-]+)"?/g)
  if (orgMatches) {
    for (const match of orgMatches) {
      // Extract the value after org:
      const colonIndex = match.indexOf(':')
      const value = match.substring(colonIndex + 1)
      // Remove quotes if present
      const orgValue = value.replace(/^"(.*)"$/, '$1')
      if (orgValue) orgs.add(orgValue)
    }
  }

  // Match repo patterns like: repo:github/subteam, repo:"github/subteam"
  const repoMatches = rawQuery.match(/repo:"?([a-zA-Z0-9-]+\/[a-zA-Z0-9-]+)"?/g)
  if (repoMatches) {
    for (const match of repoMatches) {
      // Extract the value after repo:
      const colonIndex = match.indexOf(':')
      const value = match.substring(colonIndex + 1)
      // Remove quotes if present
      const repoValue = value.replace(/^"(.*)"$/, '$1')
      // Extract org from repo format "owner/repo"
      const orgFromRepo = repoValue.split('/')[0]
      if (orgFromRepo) orgs.add(orgFromRepo)
    }
  }

  return Array.from(orgs).filter(Boolean)
}

/**
 * Parse a field suggestion or provider key to extract field name and org.
 * Uses known orgs to accurately parse hyphenated org names.
 *
 * @param input - Field suggestion or provider key to parse
 * @param knownOrgs - Set of known org names to match against
 * @returns Object with fieldName and orgName, or null if not a valid field input
 *
 * @example
 * parseFieldInput('field.priority-github:', new Set(['github']))
 * // returns {fieldName: 'priority', orgName: 'github'}
 */
export function parseFieldInput(input: string, knownOrgs: Set<string>): {fieldName: string; orgName: string} | null {
  if (!isFieldProviderKey(input)) {
    return null
  }

  // Remove "field." prefix and optional trailing colon
  const withoutFieldPrefix = input.substring(FIELD_PREFIX.length)
  const withoutColon = input.endsWith(':') ? withoutFieldPrefix.slice(0, -1) : withoutFieldPrefix

  // Try each known org to see if the input ends with it
  for (const org of knownOrgs) {
    if (withoutColon.endsWith(`-${org}`)) {
      const fieldName = withoutColon.substring(0, withoutColon.length - org.length - 1)
      if (fieldName.length > 0) {
        return {fieldName, orgName: org}
      }
    }
  }

  return null
}

/**
 * Check if a provider key represents a field provider.
 *
 * @param key - The provider key to check
 * @returns True if this is a field provider key
 *
 * @example
 * isFieldProviderKey('field.priority-github') // returns true
 * isFieldProviderKey('field.priority') // returns true
 * isFieldProviderKey('state') // returns false
 */
export function isFieldProviderKey(key: string): boolean {
  return key.startsWith(FIELD_PREFIX) && key !== FIELD_PREFIX
}

/**
 * Clean a field provider key by removing org suffix.
 * Only removes suffix if the org is in the provided context.
 *
 * @param providerKey - The original provider key
 * @param orgContext - Set of valid org names from context
 * @param knownOrgs - Set of all known org names (optional, for better matching)
 * @returns The cleaned key
 *
 * @example
 * cleanFieldProviderKey('field.priority-github', new Set(['github']))
 * // returns 'field.priority'
 *
 * cleanFieldProviderKey('field.priority-github', new Set(['microsoft']))
 * // returns 'field.priority-github' (unchanged)
 */
export function cleanFieldProviderKey(providerKey: string, orgContext: Set<string>, knownOrgs?: Set<string>): string {
  if (!isFieldProviderKey(providerKey)) {
    return providerKey
  }

  // Use known orgs if available, otherwise fall back to context orgs
  const orgsToCheck = knownOrgs || orgContext

  // Use shared parsing logic to extract field name and org
  const parseResult = parseFieldInput(providerKey, orgsToCheck)
  if (!parseResult) {
    return providerKey // No org found in provider key
  }

  const {fieldName, orgName} = parseResult

  // Only clean if the org is in the current context
  if (orgContext.has(orgName)) {
    return `field.${fieldName}`
  }

  return providerKey
}

/**
 * Clean display name by removing org suffix in parentheses.
 *
 * @param displayName - The original display name
 * @returns The cleaned display name
 *
 * @example
 * cleanDisplayName('Priority (github)') // returns 'Priority'
 * cleanDisplayName('Priority') // returns 'Priority' (unchanged)
 */
export function cleanDisplayName(displayName?: string): string {
  if (!displayName) return ''
  return displayName.replace(/\s*\([^)]+\)\s*$/, '') || displayName
}

/**
 * Check if a provider key matches any org in the context.
 * Used to determine if a field provider should be included.
 *
 * @param providerKey - The provider key to check
 * @param orgContext - Set of org names from query context
 * @returns True if the provider matches any org in context
 *
 * @example
 * providerMatchesOrgContext('field.priority-github', new Set(['github']))
 * // returns true
 *
 * providerMatchesOrgContext('field.priority-microsoft', new Set(['github']))
 * // returns false
 */
export function providerMatchesOrgContext(providerKey: string, orgContext: Set<string>): boolean {
  if (!isFieldProviderKey(providerKey)) {
    return true // Non-field providers always match
  }

  return Array.from(orgContext).some((org: string) => providerKey.includes(`-${org}`))
}

/**
 * Extract valid org names from field provider keys.
 * Useful for validating org context against available providers.
 *
 * @param providerKeys - Array of provider keys to analyze
 * @param knownOrgs - Set of known org names to match against
 * @returns Set of org names found in field providers
 *
 * @example
 * getValidOrgsFromProviders(['field.priority-github', 'field.status-microsoft', 'state'], knownDashboardOrgs)
 * // returns Set(['github', 'microsoft'])
 */
export function getValidOrgsFromProviders(providerKeys: string[], knownOrgs: Set<string>): Set<string> {
  const orgs = new Set<string>()

  // Use known orgs for accurate extraction
  for (const key of providerKeys) {
    if (isFieldProviderKey(key)) {
      const parseResult = parseFieldInput(key, knownOrgs)
      if (parseResult) {
        orgs.add(parseResult.orgName)
      }
    }
  }

  return orgs
}
